import CheckOutlined from '@ant-design/icons/CheckOutlined';
import InfoCircleOutlined from '@ant-design/icons/InfoCircleOutlined';
import Alert from 'antd/es/alert';
import Collapse from 'antd/es/collapse';
import Form, { type FormInstance } from 'antd/es/form';
import Input from 'antd/es/input';
import InputNumber from 'antd/es/input-number';
import Select from 'antd/es/select';
import Space from 'antd/es/space';
import Typography from 'antd/es/typography';
import React, { useState } from 'react';
import { useTheme } from 'styled-components';

import type { AgentFormData } from '../../AgentModals/CreateAgentModal';
import { ToolSelector } from '../ToolSelector/ToolSelector.component';
import { AGENT_TYPE_PRESETS } from '../presets';
import { FormContainer, SuggestionContainer, SuggestionTag } from './AgentInstructionsForm.styles';
import { initializeSelectedSuggestions, handleSuggestionClick } from './AgentInstructionsForm.utils';

const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;
const { Panel } = Collapse;

interface AgentInstructionsFormProps {
  form: FormInstance<AgentFormData>;
  agentType: keyof typeof AGENT_TYPE_PRESETS | 'custom';
}

export const AgentInstructionsForm: React.FC<AgentInstructionsFormProps> = ({
  form,
  agentType,
}) => {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [selectedSuggestions, setSelectedSuggestions] = useState<Set<number>>(new Set());
  const preset = agentType !== 'custom' ? AGENT_TYPE_PRESETS[agentType] : null;
  const theme = useTheme();
  
  // Initialize selected suggestions from form data on mount
React.useEffect(() => {
  initializeSelectedSuggestions(form, setSelectedSuggestions);
}, [form]);

  return (
    <FormContainer>
      <Title level={4} style={{ marginBottom: `${theme.marginXS}px` }}>
        {preset
          ? `${preset.icon} ${preset.name} Instructions`
          : '🛠️ Custom Agent Instructions'}
      </Title>
      <Paragraph
        type="secondary"
        style={{ marginBottom: `${theme.marginLG}px` }}
      >
        Tell your agent what to do and how to behave.
      </Paragraph>

      <Form.Item
        name="instructions"
        label="Agent Instructions"
        rules={[
          {
            required: true,
            message: 'Please provide instructions for your agent',
          },
          { min: 10, message: 'Instructions should be at least 10 characters' },
        ]}
        extra="Describe what your agent should do, how it should behave, and any specific guidelines"
      >
        <TextArea
          rows={8}
          placeholder={
            preset?.instructions || 'You are a helpful assistant that...'
          }
          maxLength={5000}
          showCount
        />
      </Form.Item>

      {preset && preset.suggestedPrompts.length > 0 && (
        <SuggestionContainer>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Text strong>
              <InfoCircleOutlined /> Suggested additions:
            </Text>
            <div>
              {preset.suggestedPrompts.map((prompt, index) => (
                <SuggestionTag
                  key={index}
                  color={selectedSuggestions.has(index) ? "success" : "blue"}
                  onClick={selectedSuggestions.has(index) ? undefined : () => handleSuggestionClick(prompt, index, form, selectedSuggestions, setSelectedSuggestions)}
                  style={{ 
                    cursor: selectedSuggestions.has(index) ? 'default' : 'pointer',
                    opacity: selectedSuggestions.has(index) ? 0.8 : 1
                  }}
                >
                  {selectedSuggestions.has(index) ? <CheckOutlined /> : '+'} {prompt}
                </SuggestionTag>
              ))}
            </div>
          </Space>
        </SuggestionContainer>
      )}

      <Collapse
        ghost
        style={{ marginTop: 24 }}
        onChange={(keys) => setShowAdvanced(keys.includes('1'))}
      >
        <Panel header="Advanced Options" key="1">
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            {/* System Prompt */}
            <Form.Item
              name="systemPrompt"
              label="System Prompt"
              help="Additional context or constraints that apply to all interactions"
            >
              <TextArea
                rows={3}
                placeholder="e.g., Always be professional and cite sources..."
                maxLength={2000}
                showCount
              />
            </Form.Item>

            {/* Tools Configuration */}
            <Form.Item
              name="tools"
              label="Available Tools"
              help="Select which tools this agent can use"
            >
              <ToolSelector />
            </Form.Item>

            {/* LLM Configuration */}
            <Space direction="vertical" style={{ width: '100%' }}>
              <Text strong>Model Configuration</Text>
              <Space>
                <Form.Item
                  name={['llmConfig', 'thought_llm', 'provider']}
                  label="LLM Provider"
                  style={{ marginBottom: 0 }}
                >
                  <Select
                    style={{ width: `${theme.controlHeightLG * 3.75}px` }}
                  >
                    <Select.Option value="vertexai">Vertex AI</Select.Option>
                    <Select.Option value="anthropic">Anthropic</Select.Option>
                    <Select.Option value="openai">OpenAI</Select.Option>
                  </Select>
                </Form.Item>
                <Form.Item
                  name={['llmConfig', 'thought_llm', 'model_tier']}
                  label="Model Size"
                  style={{ marginBottom: 0 }}
                >
                  <Select style={{ width: `${theme.controlHeightLG * 3}px` }}>
                    <Select.Option value="large">Large</Select.Option>
                    <Select.Option value="medium">Medium</Select.Option>
                    <Select.Option value="small">Small</Select.Option>
                  </Select>
                </Form.Item>
              </Space>
            </Space>

            {/* Execution Configuration */}
            <Space direction="vertical" style={{ width: '100%' }}>
              <Text strong>Execution Settings</Text>
              <Space wrap>
                <Form.Item
                  name={['executionConfig', 'max_actions']}
                  label="Max Actions"
                  style={{ marginBottom: 0 }}
                >
                  <InputNumber
                    min={1}
                    max={50}
                    style={{ width: `${theme.controlHeightLG * 2.5}px` }}
                  />
                </Form.Item>
                <Form.Item
                  name={['executionConfig', 'planning_interval']}
                  label="Planning Interval"
                  style={{ marginBottom: 0 }}
                >
                  <InputNumber
                    min={1}
                    max={20}
                    style={{ width: `${theme.controlHeightLG * 2.5}px` }}
                  />
                </Form.Item>
                <Form.Item
                  name={['executionConfig', 'timeout_minutes']}
                  label="Timeout (min)"
                  style={{ marginBottom: 0 }}
                >
                  <InputNumber
                    min={1}
                    max={120}
                    style={{ width: `${theme.controlHeightLG * 2.5}px` }}
                  />
                </Form.Item>
              </Space>
            </Space>
          </Space>
        </Panel>
      </Collapse>

      {showAdvanced && (
        <Alert
          message="Advanced Configuration"
          description="These settings control how your agent operates. The defaults work well for most use cases."
          type="info"
          showIcon
          style={{ marginTop: `${theme.margin}px` }}
        />
      )}
    </FormContainer>
  );
};
